import { mockLegalConsentsProps } from '../organisms/LegalConsents/__mock__/LegalConsents.mock';
import {
  ConsentError,
  ConsentErrorType,
  ConsentsSectionTypes,
  ConsentsState,
} from '../organisms/LegalConsents/ConsentsList/ConsentsList.types';
import {
  AnchorBehavior,
  ConsentCheckbox,
  ConsentHeader,
  ConsentSectionType,
  ConsentsLedger,
  LegalConsent,
} from '../organisms/LegalConsents/LegalConsents.types';
import { getEnhancedLabel } from './getEnhancedLabel';
import { isSafePropertyAssignmentKey } from './validationHelpers';

/**
 * getConsentSectionTypeAndCheckboxNameFromCheckboxId
 * ------------------------------------------------------------------------------
 * Extracts the consent section type and checkbox name from a consent checkbox ID.
 *
 * The function splits the given selected checkbox ID at the first hyphen (`-`):
 * - The substring before the first hyphen is treated as the consent section type.
 * - The substring after the first hyphen is treated as the checkbox name.
 *
 * This enables parsing IDs like:
 *   - "optional-collectPersonalDataConsentOptional"
 *   - "optional-marketing-optional-explicit-all"
 *
 * @param selectedCheckboxId - The checkbox ID string in the format "sectionType-checkboxName".
 * @returns An object containing:
 *   - selectedConsentsSectionType: The parsed consent section type.
 *   - selectedCheckboxName: The parsed checkbox name.
 */

type CheckState = { anyChecked: boolean; allChecked: boolean };
interface ProcessArgs {
  consent: LegalConsent;
  ledger: { [k: string]: boolean };
  selectedCheckboxName: string;
  state: { list: LegalConsent[]; consentsLedger: ConsentsLedger };
}

function isSectionAllChecked(ledger: Record<string, boolean>): CheckState {
  let allChecked = true,
    anyChecked = false;
  for (const key in ledger) {
    if (ledger[key]) {
      anyChecked = true;
    }
    if (!ledger[key]) {
      allChecked = false;
    }
  }
  return { anyChecked, allChecked };
}
export function processRequiredSection({ consent, ledger, selectedCheckboxName, state }: ProcessArgs): void {
  if (selectedCheckboxName != '__INIT__') {
    updateCheckbox(
      consent.checkboxes.find(cb => cb.name === selectedCheckboxName)!,
      ledger,
      !ledger[selectedCheckboxName]
    );
  }
  const { allChecked } = isSectionAllChecked(state.consentsLedger[consent.name]);
  if (!allChecked) {
    consent.hasError = true;
  } else {
    consent.hasError = false;
  }
}
export function processOptionalSection({ consent, ledger, selectedCheckboxName }: ProcessArgs): void {
  const safeLedger = ledger ?? {};
  if (selectedCheckboxName != '__INIT__') {
    const current = !!safeLedger[selectedCheckboxName];
    updateCheckbox(consent.checkboxes.find(cb => cb.name === selectedCheckboxName)!, safeLedger, !current);
  }
}
export function processUnifiedSection({ consent, ledger, selectedCheckboxName }: ProcessArgs): void {
  const [master, ...children] = consent.checkboxes;
  if (selectedCheckboxName === master.name) {
    const value = selectedCheckboxName == '__INIT__' ? ledger[master.name] : !ledger[master.name];
    for (const cb of consent.checkboxes) {
      updateCheckbox(cb, ledger, value);
    }
  } else {
    const toggledValue =
      selectedCheckboxName == '__INIT__' ? ledger[selectedCheckboxName] : !ledger[selectedCheckboxName];
    const child = children.find(c => c.name === selectedCheckboxName)!;
    updateCheckbox(child, ledger, toggledValue);

    const allChildrenChecked = children.every(c => ledger[c.name]);
    updateCheckbox(master, ledger, allChildrenChecked);
  }
}
export function processAtomicSection({ consent, ledger, selectedCheckboxName, state }: ProcessArgs): void {
  if (selectedCheckboxName != '__INIT__')
    updateCheckbox(
      consent.checkboxes.find(cb => cb.name === selectedCheckboxName)!,
      ledger,
      !ledger[selectedCheckboxName]
    );
  const { anyChecked, allChecked: sectionAllChecked } = isSectionAllChecked(state.consentsLedger[consent.name]);

  if (anyChecked && !sectionAllChecked) {
    consent.hasError = true;
  } else {
    consent.hasError = false;
  }
}

function syncValidationErrorType(state: ConsentsState) {
  let hasRequiredError = false;
  let hasAtomicError = false;

  for (const consent of state.list) {
    if (consent.sectionType === ConsentSectionType.Required && consent.hasError) {
      hasRequiredError = true;
    }
    if (consent.sectionType === ConsentSectionType.Atomic && consent.hasError) {
      hasAtomicError = true;
    }
    if (hasRequiredError && hasAtomicError) break;
  }

  if (hasRequiredError && hasAtomicError) {
    state.error.type = ConsentErrorType.MULTIPLE_VAILDATION_FAILED;
    return;
  }
  if (hasRequiredError) {
    state.error.type = ConsentErrorType.ALL_REQUIRED_CONSENTS_NOT_SELECTED;
    return;
  }
  if (hasAtomicError) {
    state.error.type = ConsentErrorType.ALL_ATOMIC_CONSENTS_NOT_SELECTED;
    return;
  }
  state.error.type = ConsentErrorType.NONE;
}
export function syncConsentState(state: ConsentsState) {
  syncAgreeAll(state);
  syncValidationErrorType(state);
}
function syncAgreeAll(state: ConsentsState) {
  let allChecked = true;

  for (const consent of state.list) {
    for (const checkbox of consent.checkboxes) {
      if (consent.sectionType == ConsentSectionType.AgreeToAll) continue;
      const checked = state.consentsLedger[consent.name][checkbox.name];
      if (!checked) {
        allChecked = false;
        break;
      }
    }
  }
  const agreeAllConsent = state.list.find(consent => consent.sectionType === ConsentSectionType.AgreeToAll);
  if (agreeAllConsent) {
    const [checkbox] = agreeAllConsent.checkboxes;
    checkbox.checked = allChecked;
    if (isSafePropertyAssignmentKey(agreeAllConsent.name)) {
      state.consentsLedger[agreeAllConsent.name][checkbox.name] = allChecked;
    }
  }
}

export function updateCheckbox(checkbox: ConsentCheckbox, ledger: { [k: string]: boolean }, value: boolean) {
  if (!checkbox) return;
  if (checkbox.showCheckbox) {
    checkbox.checked = value;
    if (isSafePropertyAssignmentKey(checkbox.name)) {
      ledger[checkbox.name] = value;
    }
  }
}
function handleAgreeAllSection(state: ConsentsState) {
  const agreeAllConsent = state.list.find(consent => consent.sectionType === ConsentSectionType.AgreeToAll)!;
  const [checkbox] = agreeAllConsent.checkboxes;

  const agreeLedger = state.consentsLedger[agreeAllConsent.name][checkbox.name];
  const shouldCheckAll = !agreeLedger;

  for (const section of state.list) {
    if (section.sectionType === ConsentSectionType.AgreeToAll) continue;

    const ledger = state.consentsLedger[section.name];

    for (const cb of section.checkboxes) {
      updateCheckbox(cb, ledger, shouldCheckAll);
    }

    // Only update hasError for Required and Atomic, others remain false
    if (section.sectionType === ConsentSectionType.Required) {
      section.hasError = !shouldCheckAll;
    } else if (section.sectionType === ConsentSectionType.Atomic) {
      section.hasError = false;
    } else {
      section.hasError = false;
    }
  }

  checkbox.checked = shouldCheckAll;
  if (isSafePropertyAssignmentKey(agreeAllConsent.name)) {
    state.consentsLedger[agreeAllConsent.name][checkbox.name] = shouldCheckAll;
  }

  return shouldCheckAll;
}

export function toggleSelectedConsentCheckbox({
  consentState,
  selectedCheckboxName,
  sectionType,
}: {
  consentState: ConsentsState;
  selectedCheckboxName: string;
  sectionType: string;
}): ConsentsState {
  const { consentsLedger, list } = consentState;

  if (sectionType === ConsentSectionType.AgreeToAll) {
    handleAgreeAllSection(consentState);
    syncConsentState(consentState);
    return consentState;
  }

  const consent = list.find(c => c.checkboxes.some(cb => cb.name === selectedCheckboxName));
  if (!consent) {
    syncConsentState(consentState);
    return consentState;
  }

  //Ensure ledger exists for this consent.name
  consentsLedger[consent.name] ??= {};
  const ledger = consentsLedger[consent.name];

  if (consent.sectionType !== sectionType) {
    syncConsentState(consentState);
    return consentState;
  }

  switch (sectionType) {
    case ConsentSectionType.Required:
      processRequiredSection({ consent, ledger, selectedCheckboxName, state: consentState });
      break;

    case ConsentSectionType.Optional:
      processOptionalSection({ consent, ledger, selectedCheckboxName, state: consentState });
      break;

    case ConsentSectionType.Unified:
      processUnifiedSection({ consent, ledger, selectedCheckboxName, state: consentState });
      break;

    case ConsentSectionType.Atomic:
      processAtomicSection({ consent, ledger, selectedCheckboxName, state: consentState });
      break;

    default:
      break;
  }

  syncConsentState(consentState);
  return consentState;
}

export function getApplicableAndSortedConsents({
  allConsents,
  preStateLedger = {},
  countryCode,
  option,
  anchorBehavior,
  ariaModalText,
  ariaWindowText,
}: {
  allConsents: LegalConsent[];
  preStateLedger?: ConsentsLedger;
  countryCode: string;
  option: string;
  anchorBehavior: AnchorBehavior;
  ariaModalText: string | null;
  ariaWindowText: string | null;
}): ConsentsState {
  let filteredConsents: LegalConsent[] = [];
  let sectionTypes: ConsentsSectionTypes = {};
  let isAgreeAllConsentCheckboxPreChecked = false;
  const ledger = {} as ConsentsLedger;

  const response: ConsentsState = {
    list: [],
    sectionTypes: {},
    consentsLedger: ledger,
    error: { type: ConsentErrorType.NONE, message: null },
  };

  const validateAndFilterConsentsListResponse = validateAndFilterConsentsList({
    allConsents,
    countryCode,
    option,
    sortEntries: true,
  });

  if (validateAndFilterConsentsListResponse.error.type !== ConsentErrorType.NONE) {
    response.error = validateAndFilterConsentsListResponse.error;
    return response;
  }

  filteredConsents = validateAndFilterConsentsListResponse.filteredList;
  sectionTypes = validateAndFilterConsentsListResponse.sectionTypes;

  if (sectionTypes[ConsentSectionType.AgreeToAll]) {
    const agreeAllConsentCheckboxes =
      validateAndFilterConsentsListResponse[ConsentSectionType.AgreeToAll]?.checkboxes?.[0];
    isAgreeAllConsentCheckboxPreChecked = agreeAllConsentCheckboxes?.defaultChecked ? true : false;
  }

  for (const consent of filteredConsents) {
    const { headers, checkboxes } = consent;

    const filteredHeaders = getApplicableAndSortedConsentHeaders({
      headers,
      countryCode,
      option,
      sortEntries: true,
    });

    const { filteredCheckboxes, isAllTheCheckboxesSelected } = getApplicableAndSortedConsentCheckboxes({
      checkboxes,
      preStateLedger,
      ledger,
      countryCode,
      sectionType: consent.sectionType as ConsentSectionType,
      option,
      isAgreeAllConsentCheckboxPreChecked,
      sortEntries: true,
      anchorBehavior,
      ariaModalText,
      ariaWindowText,
      sectionName: consent.name,
    });

    // If required consents are not selected then error state will be updated with ConsentsError.ALL_REQUIRED_CONSENTS_NOT_SELECTED
    if (consent.sectionType === ConsentSectionType.Required && !isAllTheCheckboxesSelected) {
      response.error.type = ConsentErrorType.ALL_REQUIRED_CONSENTS_NOT_SELECTED;
    }
    //Implement handlers over here
    consent.headers = filteredHeaders;
    consent.checkboxes = filteredCheckboxes;
    consent.hasError = false; // default for all consent type
    const spreadedLedger = ledger[consent.name];
    const consentState = { list: filteredConsents, consentsLedger: ledger };
    if (consent.sectionType == ConsentSectionType.Required) {
      processRequiredSection({
        consent,
        ledger: spreadedLedger,
        selectedCheckboxName: '__INIT__',
        state: consentState,
      });
    }
    if (consent.sectionType == ConsentSectionType.Optional) {
      processOptionalSection({
        consent,
        ledger: spreadedLedger,
        selectedCheckboxName: '__INIT__',
        state: consentState,
      });
    }

    if (consent.sectionType == ConsentSectionType.Unified) {
      processUnifiedSection({
        consent,
        ledger: spreadedLedger,
        selectedCheckboxName: '__INIT__',
        state: consentState,
      });
    }
    if (consent.sectionType == ConsentSectionType.Atomic) {
      processAtomicSection({
        consent,
        ledger: spreadedLedger,
        selectedCheckboxName: '__INIT__',
        state: consentState,
      });
    }
  }

  response.list = filteredConsents;
  response.sectionTypes = sectionTypes;
  response.consentsLedger = ledger;
  syncConsentState(response);
  return response;
}

function getApplicableAndSortedConsentHeaders({
  headers,
  countryCode,
  option,
  sortEntries = false,
}: {
  headers: ConsentHeader[];
  countryCode: string;
  option: string;
  sortEntries?: boolean;
}): ConsentHeader[] {
  let filteredHeaders = [] as ConsentHeader[];

  if (headers.length < 1) {
    return headers;
  }

  for (const header of headers) {
    const { forCountries, excludeCountries, forOptions, excludeOptions } = header;

    const isHeaderToBeSkipped = checkIfElementToBeSkippedByCountryAndOptions({
      countryCode,
      option,
      forCountries,
      excludeCountries,
      forOptions,
      excludeOptions,
    });

    // Skipping the entry if the conditions are not met
    if (isHeaderToBeSkipped) {
      continue;
    }

    filteredHeaders.push({ ...header });
  }

  if (sortEntries && filteredHeaders.length > 1) {
    filteredHeaders = sortArrayByField({ data: filteredHeaders, sortField: 'index' });
  }

  return filteredHeaders;
}

function getApplicableAndSortedConsentCheckboxes({
  checkboxes,
  preStateLedger,
  ledger,
  sectionType,
  countryCode,
  option,
  sortEntries = false,
  isAgreeAllConsentCheckboxPreChecked,
  anchorBehavior,
  ariaModalText,
  ariaWindowText,
  sectionName,
}: {
  checkboxes: ConsentCheckbox[];
  preStateLedger: ConsentsLedger;
  ledger: ConsentsLedger;
  sectionType: ConsentSectionType;
  countryCode: string;
  option: string;
  sortEntries?: boolean;
  isAgreeAllConsentCheckboxPreChecked: boolean;
  anchorBehavior: AnchorBehavior;
  ariaModalText: string | null;
  ariaWindowText: string | null;
  sectionName: string;
}) {
  let filteredCheckboxes: ConsentCheckbox[] = [];
  let isAllTheCheckboxesSelected = true;

  for (const _checkbox of checkboxes) {
    const checkbox = { ..._checkbox };
    const { forCountries, excludeCountries, forOptions, excludeOptions } = checkbox;

    const isCheckboxToBeSkipped = checkIfElementToBeSkippedByCountryAndOptions({
      countryCode,
      option,
      forCountries,
      excludeCountries,
      forOptions,
      excludeOptions,
    });

    // Skipping the entry if the conditions are not met
    if (isCheckboxToBeSkipped) {
      continue;
    }

    const _preStateCheckboxValue = preStateLedger?.[sectionType]?.[checkbox.name];
    const preStateCheckboxValue = _preStateCheckboxValue ?? null;

    const isChecked = determineIsChecked({
      preStateCheckboxValue,
      checkboxes,
      isAgreeAllConsentCheckboxPreChecked,
      sectionType,
      isCheckboxDefaultChecked: checkbox.defaultChecked,
    });

    const checkboxLabel = getEnhancedLabel({
      label: checkbox.label.markdown,
      anchorBehavior,
      ariaModalText,
      ariaWindowText,
    });

    checkbox.label = { markdown: checkboxLabel };

    checkbox.checked = isChecked;

    filteredCheckboxes.push({ ...checkbox });

    if (!isChecked) {
      isAllTheCheckboxesSelected = false;
    }

    // ledger passed as reference so updating will not require re-assigning

    if (ledger[sectionName]) {
      ledger[sectionName][checkbox.name] = checkbox.checked;
    } else {
      ledger[sectionName] = { [checkbox.name]: checkbox.checked };
    }
  }

  if (sortEntries && filteredCheckboxes.length > 1) {
    filteredCheckboxes = sortArrayByField({ data: filteredCheckboxes, sortField: 'index' });
  }

  return {
    filteredCheckboxes,
    isAllTheCheckboxesSelected,
    ledger,
  };
}

interface DetermineIsCheckedProps {
  preStateCheckboxValue: boolean | null;
  isAgreeAllConsentCheckboxPreChecked: boolean;
  sectionType: ConsentSectionType;
  checkboxes: ConsentCheckbox[];
  isCheckboxDefaultChecked: boolean;
}

function determineIsChecked({
  preStateCheckboxValue,
  isAgreeAllConsentCheckboxPreChecked,
  sectionType,
  checkboxes,
  isCheckboxDefaultChecked,
}: DetermineIsCheckedProps): boolean {
  const hasPreStateValue = preStateCheckboxValue !== undefined && preStateCheckboxValue !== null;
  const isUnifiedWithMasterChecked = sectionType === ConsentSectionType.Unified && !!checkboxes[0]?.defaultChecked; // master is always at indexed 0

  if (hasPreStateValue) return preStateCheckboxValue;
  if (isAgreeAllConsentCheckboxPreChecked) return true;
  if (isUnifiedWithMasterChecked) return true;
  if (isCheckboxDefaultChecked) return true;

  return false;
}

// Returns true or false based in the input
export function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined || value === '') {
    return true;
  }
  if (Array.isArray(value) && value.length === 0) {
    return true;
  }
  if (typeof value === 'object' && Object.keys(value).length === 0) {
    return true;
  }
  return false;
}

function sortArrayByField<T, U extends keyof T>({ data, sortField }: { data: Array<T>; sortField: U }) {
  data = data?.sort((a, b) => {
    if (a[sortField] < b[sortField]) {
      return -1;
    }
    if (a[sortField] > b[sortField]) {
      return 1;
    }
    return 0;
  });

  return data;
}

export async function getLegalConsents() {
  return mockLegalConsentsProps;
}

function checkIfElementToBeSkippedByCountryAndOptions({
  countryCode,
  option,
  forCountries,
  excludeCountries,
  forOptions,
  excludeOptions,
}: {
  countryCode: string;
  option: string;
  forCountries: string[] | null;
  excludeCountries: string[] | null;
  forOptions: string[] | null;
  excludeOptions: string[] | null;
}) {
  const isValueInList = (list: string[] | null, value: string, shouldBeInTheList: boolean): boolean => {
    // Always check for isEmpty first
    if (list && list.length > 0) {
      return shouldBeInTheList ? !list.includes(value) : list.includes(value);
    }
    return false;
  };

  // If forCountries field does not exists or empty
  // Then considering that by default it is applied for all the country code that are tagged at the consent section level
  // If forCountries has the values and it does not match with the current country code then we have to skip this checkbox entry
  if (isValueInList(forCountries, countryCode, true) || isValueInList(excludeCountries, countryCode, false)) {
    return true;
  }

  // If forOptions field does not exists or empty
  // Then considering that by default it is applied for all the options that are tagged at the consent section level
  // If forOptions has the values and it does not match with the current country code then we have to skip this checkbox entry
  if (isValueInList(forOptions, option, true) || isValueInList(excludeOptions, option, false)) {
    return true;
  }

  return false;
}

const ErrorMessages = {
  A_LA_CARTE_CHECKBOXES: `Internal Validation Error: Unified consents must contain more than one checkbox. Please ensure multiple checkbox entries are present, or consider using the 'Other' section if only one checkbox is required.`,
  AGREE_ALL_CHECKBOXES: `Internal Validation Error: AgreeAll consents cannot have more than one checkbox, please ensure there is only one checkbox entry present.`,
  AT_LEAST_ONE_FIELD_PRESENT: (fieldsList: string[], context: string, name: string) =>
    `Internal Validation Error: Invalid entry found for '${name}', At least one of the following properties must be present in '${context}': ${fieldsList.join(
      ', '
    )}`,
  EMPTY_CONSENT: `Internal Validation Error: Consent entry cannot be empty, please ensure consents has all required fields present on the object.`,
  EMPTY_COUNTRIES: (name: string) =>
    `Internal Validation Error: Property 'countries' in 'Consent' for '${name}' cannot be empty, please ensure all the mandatory fields are filled with valid values/data.`,
  EMPTY_FIELD: (field: string, context: string, name: string) =>
    `Internal Validation Error: Property '${field}' in '${context}' for '${name}' cannot be empty, please ensure all the mandatory fields are filled with valid values/data.`,
  EMPTY_OPTIONS: (name: string) =>
    `Internal Validation Error: Property 'options' in 'Consent' for '${name}' cannot be empty, please ensure all the mandatory fields are filled with valid values/data.`,
  TERMS_LINK: (name: string) =>
    `Internal Validation Error: Property 'termsLink' for '${name}' cannot be empty, please ensure 'termsLink' is filled with valid values/data.`,
  TERMS_LINK_LABEL: (name: string) =>
    `Internal Validation Error: Property 'termsLinkLabel' for '${name}' cannot be empty, please ensure 'termsLinkLabel' is filled with valid values/data.`,
  SHOW_CHECKBOX_LABEL: (name: string, sectionName: string) =>
    `Hidden checkboxes must be Default checked (defaultChecked: true). checkboxName: ${name}, sectionName: ${sectionName}`,
  DUPLICATE_CHECKBOX_NAME_LABEL: (name: string, sectionName: string) =>
    `Duplicate checkbox names found. checkboxName: ${name},  sectionName: ${sectionName}`,
  DUPLICATE_SECTION_NAME_LABEL: (sectionName: string) => `Duplicate section Names found. sectionName: ${sectionName}`,
  DUPLICATE_SECTION_ID_LABEL: (sectionId: string) => `Duplicate section Ids found. sectionId: ${sectionId}`,
};

function generateValidationError(message: string): ValidationError {
  return {
    type: ConsentErrorType.INTERNAL_VALIDATION_ERROR,
    message,
  };
}

interface ValidateAndFilterConsentsListParams {
  allConsents: LegalConsent[];
  countryCode: string;
  option: string;
  sortEntries: true;
}
interface ValidateAndFilterConsentsListResponse {
  filteredList: LegalConsent[];
  error: ConsentError;
  sectionTypes: ConsentsSectionTypes;
  [ConsentSectionType.AgreeToAll]: LegalConsent;
}

function validateAndFilterConsentsList({
  allConsents,
  countryCode,
  option,
  sortEntries,
}: ValidateAndFilterConsentsListParams): ValidateAndFilterConsentsListResponse {
  let applicableConsents = [] as LegalConsent[];
  const sectionTypes: ConsentsSectionTypes = {};
  const reponse: ValidateAndFilterConsentsListResponse = {
    filteredList: [],
    error: { type: ConsentErrorType.NONE, message: null },
    sectionTypes: {},
    [ConsentSectionType.AgreeToAll]: {} as LegalConsent,
  };

  const duplicatesSectionIds: Record<string, boolean> = {};
  const duplicatesSectionNames: Record<string, boolean> = {};
  for (const consent of allConsents) {
    if (isEmpty(consent)) {
      reponse.error = generateValidationError(ErrorMessages.EMPTY_CONSENT);
      return reponse;
    }

    const { id, name, countries, options } = consent;

    if (isEmpty(countries)) {
      reponse.error = generateValidationError(ErrorMessages.EMPTY_COUNTRIES(name));
      return reponse;
    }

    if (isEmpty(options)) {
      reponse.error = generateValidationError(ErrorMessages.EMPTY_OPTIONS(name));
      return reponse;
    }

    if (duplicatesSectionIds[id]) {
      reponse.error = generateValidationError(ErrorMessages.DUPLICATE_SECTION_ID_LABEL(id));
      return reponse;
    } else {
      duplicatesSectionIds[id] = true;
    }

    if (duplicatesSectionNames[name]) {
      reponse.error = generateValidationError(ErrorMessages.DUPLICATE_SECTION_NAME_LABEL(name));
      return reponse;
    } else {
      duplicatesSectionNames[name] = true;
    }

    const isCountryMatched = countries.indexOf(countryCode) !== -1;
    const isOptionMatched = options.indexOf(option) !== -1;
    if (isCountryMatched && isOptionMatched) {
      sectionTypes[consent.sectionType] = consent.sectionType;
      const validationErrorForConsent = ValidateAllMandatoryFieldsArePresentForConsent({ consent });

      // Return with error state if invalid format or missing fields found
      if (validationErrorForConsent.type !== ConsentErrorType.NONE) {
        reponse.error = validationErrorForConsent;
        return reponse;
      }

      const headers = consent.headers;
      if (!isEmpty(headers)) {
        for (const header of headers) {
          const validationErrorForConsentHeader = ValidateAllMandatoryFieldsArePresentForConsentHeader({
            header,
          });

          if (validationErrorForConsentHeader.type !== ConsentErrorType.NONE) {
            reponse.error = validationErrorForConsentHeader;
            return reponse;
          }
        }
      }

      const checkboxes = consent.checkboxes;
      if (!isEmpty(checkboxes)) {
        const duplicateCheckboxNames: Record<string, boolean> = {};
        for (const checkbox of checkboxes) {
          const validationErrorForConsentCheckbox = ValidateAllMandatoryFieldsArePresentForConsentCheckbox({
            checkbox,
          });

          if (validationErrorForConsentCheckbox.type !== ConsentErrorType.NONE) {
            reponse.error = validationErrorForConsentCheckbox;
            return reponse;
          }
          if (duplicateCheckboxNames[checkbox.name]) {
            reponse.error = generateValidationError(
              ErrorMessages.DUPLICATE_CHECKBOX_NAME_LABEL(checkbox.name, consent.name)
            );
            return reponse;
          } else {
            duplicateCheckboxNames[checkbox.name] = true;
          }

          if (checkbox.showCheckbox === false && checkbox.defaultChecked !== true) {
            reponse.error = generateValidationError(ErrorMessages.SHOW_CHECKBOX_LABEL(checkbox.name, consent.name));
            return reponse;
          }
        }
      }

      if (consent.sectionType === ConsentSectionType.AgreeToAll) {
        reponse[ConsentSectionType.AgreeToAll] = consent;
      }
      applicableConsents.push({ ...consent });
    }
  }

  if (sortEntries) {
    applicableConsents = sortArrayByField({ data: applicableConsents, sortField: 'index' });
  }
  reponse.filteredList = applicableConsents;
  reponse.sectionTypes = sectionTypes;
  return reponse;
}

interface ValidationError {
  type: ConsentErrorType;
  message: string | null;
}

interface ValidationParams {
  fields: string[];
  data: LegalConsent | ConsentHeader | ConsentCheckbox;
  context: string;
}

function validateMandatoryFields({
  fields,
  data,
  context,
  name,
}: ValidationParams & { name: string }): ValidationError {
  for (const field of fields) {
    const value = data[field as keyof typeof data];
    if (isEmpty(value)) {
      return generateValidationError(ErrorMessages.EMPTY_FIELD(field, context, name));
    }
  }
  return { type: ConsentErrorType.NONE, message: null };
}

function validateAtLeastOneFieldPresent({
  fields,
  data,
  context,
  name,
}: ValidationParams & { name: string }): ValidationError {
  for (const field of fields) {
    if (!isEmpty(data[field as keyof typeof data])) {
      return { type: ConsentErrorType.NONE, message: null };
    }
  }
  return generateValidationError(ErrorMessages.AT_LEAST_ONE_FIELD_PRESENT(fields, context, name));
}

export const mandatoryFieldsForConsents = ['id', 'name', 'countries', 'options', 'sectionType', 'checkboxes'];
function ValidateAllMandatoryFieldsArePresentForConsent({ consent }: { consent: LegalConsent }): ValidationError {
  const validationError = validateMandatoryFields({
    fields: mandatoryFieldsForConsents,
    data: consent,
    context: 'Consent',
    name: consent.name,
  });

  // Return error if validation of mandatory fields fails
  if (validationError.type !== ConsentErrorType.NONE) {
    return validationError;
  }

  const isSectionTypeIsAgreeAll = consent.sectionType === ConsentSectionType.AgreeToAll;
  const hasMultipleCheckboxes = consent.checkboxes.length > 1;
  // AgreeToAll section type should have only one checkbox
  if (isSectionTypeIsAgreeAll && hasMultipleCheckboxes) {
    return generateValidationError(ErrorMessages.AGREE_ALL_CHECKBOXES);
  }

  const isSectionTypeUnified = consent.sectionType === ConsentSectionType.Unified;
  const hasOnlyOneCheckbox = consent.checkboxes.length === 1;
  // ALaCarte section type must have more than one checkbox(s)
  if (isSectionTypeUnified && hasOnlyOneCheckbox) {
    return generateValidationError(ErrorMessages.A_LA_CARTE_CHECKBOXES);
  }

  return validationError;
}

export const MandatoryFieldsForHeader = ['id', 'name'];
function ValidateAllMandatoryFieldsArePresentForConsentHeader({ header }: { header: ConsentHeader }): ValidationError {
  let error = validateMandatoryFields({
    fields: MandatoryFieldsForHeader,
    data: header,
    context: 'ConsentHeader',
    name: header.name,
  });
  if (error.type !== ConsentErrorType.NONE) return error;

  const LeastOneFieldPresentForHeader = ['header', 'subHeader', 'description'];
  error = validateAtLeastOneFieldPresent({
    fields: LeastOneFieldPresentForHeader,
    data: header,
    context: 'ConsentHeader',
    name: header.name,
  });
  return error;
}

export const MandatoryFieldsForCheckbox = ['name', 'isDisabled', 'defaultChecked', 'showCheckbox', 'label'];
function ValidateAllMandatoryFieldsArePresentForConsentCheckbox({
  checkbox,
}: {
  checkbox: ConsentCheckbox;
}): ValidationError {
  const error = validateMandatoryFields({
    fields: MandatoryFieldsForCheckbox,
    data: checkbox,
    context: 'ConsentCheckbox',
    name: checkbox.name,
  });
  if (error.type !== ConsentErrorType.NONE) return error;

  const { termsLink, termsLinkLabel } = checkbox;
  if (termsLink && !termsLinkLabel) {
    return generateValidationError(ErrorMessages.TERMS_LINK_LABEL(checkbox.name));
  }
  if (termsLinkLabel && !termsLink) {
    return generateValidationError(ErrorMessages.TERMS_LINK(checkbox.name));
  }
  return { type: ConsentErrorType.NONE, message: null };
}
